import type { CompanyId } from 'common/api/api-types';
import type { z } from 'zod';

import { useLocalStorage } from '@uidotdev/usehooks';
import { useEffectEvent } from 'react';

import { noop } from 'common/utils';
import { useAuthState } from 'common/utils/authenticatorUtils';

type Version = 'v1' | 'v2';

type FieldsByPage = {
  allCouriers:
    | 'teams'
    | 'vehicleType'
    | 'courierGroup'
    | 'hideExpiredCouriers'
    | 'hideInactiveCouriers';
  performance: 'teams' | 'vehicleType' | 'courierGroup' | 'hideExpiredCouriers' | 'timeframe';
  financials: 'teams' | 'vehicleType' | 'courierGroup' | 'hideExpiredCouriers' | 'timeframe';
  liveDeliveries: 'teams' | 'vehicleType' | 'courierGroup';
  geoLocationHistory: 'teams' | 'vehicleType' | 'courierGroup' | 'couriers';
  schedule: 'teams' | 'vehicleType' | 'courierGroup' | 'date' | 'startingPoint';
  overview: 'timeframe' | 'cities';
  lcspOverview: 'dateRange';
};

type Page = keyof FieldsByPage;

type AllowedValue = string | string[];

type UsePersistedStateOptions<S extends z.ZodType<unknown, AllowedValue>> = {
  version: Version;
  companyId?: CompanyId;
  page: Page;
  field: FieldsByPage[Page];
  schema: S;
  fallback?: z.output<S>;
};

function usePersistedState<S extends z.ZodType<unknown, AllowedValue>>({
  version,
  companyId,
  page,
  field,
  schema,
  fallback,
}: UsePersistedStateOptions<S>): [
  z.output<S> | undefined,
  (value: z.output<S> | undefined) => void,
] {
  const { user } = useAuthState();
  const userId = user?.id;
  const key = `wdos:usr/${userId ?? '<unknown>'}:co/${companyId}:${page}:${version}:filters:${field}`;
  const [raw, setRaw] = useLocalStorage<unknown>(key, undefined);

  const setter = useEffectEvent((value: z.output<S> | undefined) => {
    const next = value === undefined ? undefined : schema.encode(value);
    setRaw(next);
  });

  if (!userId || !companyId) {
    return [fallback, noop];
  }

  const parsed = schema.safeParse(raw);
  const value = parsed.success ? parsed.data : fallback;
  return [value, setter];
}

export default usePersistedState;
